import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Add, Check, Close, Cancel, Search } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SolicitacaoAbastecimento, Veiculo } from '../types';
import { solicitacaoService } from '../services/solicitacaoService';
import { veiculoService } from '../services/veiculoService';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';

const Solicitacoes: React.FC = () => {
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoAbastecimento[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [open, setOpen] = useState(false);
  const [openAprovacao, setOpenAprovacao] = useState(false);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState<SolicitacaoAbastecimento | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const { user, hasRole } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [filtro, setFiltro] = useState('');

  const [novaSolicitacao, setNovaSolicitacao] = useState({
    veiculoId: 0,
    quilometragemAtual: 0,
    litrosSolicitados: 0,
    valorEstimado: 0,
    observacao: '',
  });

  useEffect(() => {
    loadSolicitacoes();
    loadVeiculos();
  }, []);

  const loadSolicitacoes = async () => {
    try {
      const data = await solicitacaoService.listar();
      setSolicitacoes(data);
    } catch (error) {
      console.error('Erro ao carregar solicitações', error);
    }
  };

  const loadVeiculos = async () => {
    try {
      if (hasRole('MOTORISTA')) {
        const data = await veiculoService.listarMeus();
        setVeiculos(data);
      } else {
        const data = await veiculoService.listarPorStatus('ATIVO');
        setVeiculos(data);
      }
    } catch (error) {
      console.error('Erro ao carregar veículos', error);
    }
  };

  const handleCriar = async () => {
    setSalvando(true);
    try {
      await solicitacaoService.criar(novaSolicitacao);
      loadSolicitacoes();
      setOpen(false);
      setNovaSolicitacao({ veiculoId: 0, quilometragemAtual: 0, litrosSolicitados: 0, valorEstimado: 0, observacao: '' });
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleAprovar = async () => {
    if (selectedSolicitacao?.id) {
      setSalvando(true);
      try {
        await solicitacaoService.aprovar(selectedSolicitacao.id);
        loadSolicitacoes();
        setOpenAprovacao(false);
        setSelectedSolicitacao(null);
      } catch (error) {
        setErro(getErrorMessage(error));
      } finally {
        setSalvando(false);
      }
    }
  };

  const handleReprovar = async () => {
    if (selectedSolicitacao?.id) {
      setSalvando(true);
      try {
        await solicitacaoService.reprovar(selectedSolicitacao.id, motivo);
        loadSolicitacoes();
        setOpenAprovacao(false);
        setSelectedSolicitacao(null);
        setMotivo('');
      } catch (error) {
        setErro(getErrorMessage(error));
      } finally {
        setSalvando(false);
      }
    }
  };

  const handleCancelar = (id: number) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete.id) return;
    try {
      await solicitacaoService.cancelar(confirmDelete.id);
      loadSolicitacoes();
      setConfirmDelete({ open: false, id: null });
    } catch (error) {
      console.error('Erro ao cancelar solicitação', error);
      setConfirmDelete({ open: false, id: null });
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'PENDENTE': return 'warning';
      case 'APROVADA': return 'success';
      case 'REPROVADA': return 'error';
      case 'CANCELADA': return 'default';
      default: return 'default';
    }
  };

  const termo = filtro.trim().toLowerCase();
  const solicitacoesFiltradas = termo
    ? solicitacoes.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : solicitacoes;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Solicitações de Abastecimento</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          {hasRole('MOTORISTA') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpen(true)}
            >
              Nova Solicitação
            </Button>
          )}
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Veículo</TableCell>
              <TableCell>Motorista</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Litros</TableCell>
              <TableCell>Valor Est.</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {solicitacoesFiltradas.map((sol) => (
              <TableRow key={sol.id}>
                <TableCell>{sol.id}</TableCell>
                <TableCell>{sol.veiculoPlaca} - {sol.veiculoModelo}</TableCell>
                <TableCell>{sol.motoristaNome}</TableCell>
                <TableCell>{sol.dataSolicitacao ? new Date(sol.dataSolicitacao).toLocaleString('pt-BR') : '-'}</TableCell>
                <TableCell>{sol.litrosSolicitados} L</TableCell>
                <TableCell>R$ {sol.valorEstimado?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                <TableCell>
                  <Chip
                    label={sol.status}
                    color={getStatusColor(sol.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {sol.status === 'PENDENTE' && (hasRole('ADMIN') || hasRole('GERENTE')) ? (
                    <>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => { setSelectedSolicitacao(sol); setOpenAprovacao(true); }}
                      >
                        <Check />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => { setSelectedSolicitacao(sol); setOpenAprovacao(true); }}
                      >
                        <Close />
                      </IconButton>
                    </>
                  ) : null}
                  {sol.status === 'PENDENTE' && hasRole('MOTORISTA') && (
                    <IconButton
                      size="small"
                      onClick={() => sol.id && handleCancelar(sol.id)}
                    >
                      <Cancel />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Solicitação de Abastecimento</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Veículo"
            value={novaSolicitacao.veiculoId}
            onChange={(e) => {
              const veiculoId = Number(e.target.value);
              const veiculo = veiculos.find(v => v.id === veiculoId);
              setNovaSolicitacao({
                ...novaSolicitacao,
                veiculoId,
                quilometragemAtual: veiculo?.quilometragemAtual || 0,
              });
            }}
            margin="normal"
          >
            {veiculos.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.placa} - {v.marca} {v.modelo}
              </MenuItem>
            ))}
          </TextField>

          {novaSolicitacao.veiculoId > 0 && (
            <TextField
              fullWidth
              label="Quilometragem Atual do Veículo"
              value={veiculos.find(v => v.id === novaSolicitacao.veiculoId)?.quilometragemAtual?.toLocaleString('pt-BR') || '0'}
              margin="normal"
              InputProps={{ readOnly: true }}
              helperText="Quilometragem registrada no sistema"
            />
          )}

          <TextField
            fullWidth
            label="Quilometragem no Momento"
            type="number"
            value={novaSolicitacao.quilometragemAtual}
            onChange={(e) => setNovaSolicitacao({ ...novaSolicitacao, quilometragemAtual: Number(e.target.value) })}
            margin="normal"
            error={novaSolicitacao.veiculoId > 0 && novaSolicitacao.quilometragemAtual < (veiculos.find(v => v.id === novaSolicitacao.veiculoId)?.quilometragemAtual || 0)}
            helperText={
              novaSolicitacao.veiculoId > 0 && novaSolicitacao.quilometragemAtual < (veiculos.find(v => v.id === novaSolicitacao.veiculoId)?.quilometragemAtual || 0)
                ? 'Quilometragem não pode ser menor que a atual do veículo'
                : 'Km no momento do abastecimento'
            }
          />
          <TextField
            fullWidth
            label="Litros Solicitados"
            type="number"
            value={novaSolicitacao.litrosSolicitados}
            onChange={(e) => setNovaSolicitacao({ ...novaSolicitacao, litrosSolicitados: Number(e.target.value) })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Valor Estimado"
            type="number"
            value={novaSolicitacao.valorEstimado}
            onChange={(e) => setNovaSolicitacao({ ...novaSolicitacao, valorEstimado: Number(e.target.value) })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Observação"
            value={novaSolicitacao.observacao}
            onChange={(e) => setNovaSolicitacao({ ...novaSolicitacao, observacao: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCriar}
            variant="contained"
            disabled={
              salvando ||
              !novaSolicitacao.veiculoId ||
              novaSolicitacao.quilometragemAtual < (veiculos.find(v => v.id === novaSolicitacao.veiculoId)?.quilometragemAtual || 0)
            }
          >
            {salvando ? 'Enviando...' : 'Solicitar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAprovacao} onClose={() => setOpenAprovacao(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSolicitacao?.status === 'PENDENTE' ? 'Aprovar/Reprovar Solicitação' : 'Detalhes'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Motivo (opcional para aprovação, obrigatório para reprovação)"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAprovacao(false)}>Fechar</Button>
          <Button onClick={handleReprovar} color="error" variant="outlined" disabled={salvando}>
            {salvando ? 'Rejeitando...' : 'Reprovar'}
          </Button>
          <Button onClick={handleAprovar} color="success" variant="contained" disabled={salvando}>
            {salvando ? 'Aprovando...' : 'Aprovar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Confirmar cancelamento"
        message="Tem certeza que deseja cancelar esta solicitação?"
        confirmText="Cancelar"
        severity="warning"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />
    </Container>
  );
};

export default Solicitacoes;
